import { withLDConsumer } from 'launchdarkly-react-client-sdk';
import isNumber from 'lodash/isNumber';
import isObject from 'lodash/isObject';
import PropTypes from 'prop-types';
import React, { Component } from 'react';
import Chatbot from '../chatbot';
import Data from '../data';
import { InternalCapability } from '../data/account';
import Grid from '../grid';
import Modal from '../modal';
import UI from '../ui';
import Utils from '../utils';

import neutralizeCsvFormula from '../utils/neutralizeCsvFormula';
import AddCostCodesModal from './AddCostCodesModal.component';
import DeleteCostCodesModal from './DeleteCostCodesModal.component';
import ImportCostCodesModal from './ImportCostCodesModal.component';

const { ModalContext, ModalButtonActionType } = Modal;
const { Session, Logger, Communication, BillingPlans, Util } = Utils;
const { Spinner, WrappedToolbar, WrappedButtonType } = UI;
const { GridPanel } = Grid;
const { ChatbotTriggers, launchChatbot } = Chatbot;

// RFC 4180 quoting, applied only when needed so ordinary codes export unchanged.
const toCsvField = code => {
	const field = neutralizeCsvFormula(`${code}`);
	return /[",\r\n]/.test(field) ? `"${field.replace(/"/g, '""')}"` : field;
};

class CostCodes extends Component {
	constructor(props) {
		super(props);

		const { enterpriseLocation } = props;
		const { customerId, planDetails = {} } = Session.getAccountInfo();
		const { costCodeLimit } = planDetails;

		this.state = {
			rowData: [],
			columnDefs: [
				{
					headerName: `Cost Code`,
					field: 'Code',
					headerCheckboxSelection: true,
					checkboxSelection: true,
					suppressSizeToFit: false,
					flex: 1, // cost code column should be full width
				},
			],
			codeLimit:
				!isNumber(costCodeLimit) || costCodeLimit === 0 ? 1000 : costCodeLimit, // per WEBAPPS-25580, treat limit as 1000 if the costCodeLimit=0
			numberOfRows: 0,
			customerId: isObject(enterpriseLocation)
				? enterpriseLocation.CustId
				: customerId,
			isLoading: false,
			loadingMessage: '',
		};

		this.gridApi = null;

		Session.subscribe(() => {
			const { rowData, columnDefs } = this.state;
			const account = Session.getAccountInfo();
			const codeLimit = account.planDetails?.costCodeLimit;
			const remainingCodes =
				codeLimit - rowData.length < 0 ? 0 : codeLimit - rowData.length;
			const newColumnDefs = [
				{
					...columnDefs[0],
					headerName: `Cost Code (${remainingCodes} More Available)`,
				},
			];
			this.setState(
				{
					codeLimit: codeLimit === 0 ? 1000 : codeLimit, // per WEBAPPS-25580, treat limit as 1000 if the costCodeLimit=0
					remainingCodes,
				},
				() => {
					this.gridApi.setColumnDefs(newColumnDefs);
					this.gridApi.sizeColumnsToFit();
				}
			);
		});
	}

	/**
	 * Make an API call to fetch cost codes.
	 * @param {func} callback Notify the caller about the new cost codes.
	 */
	fetchCostCodes = async callback => {
		const { showError } = this.context;
		const { codeLimit, columnDefs, customerId } = this.state;
		const response = await Communication.getCostCodes(customerId);

		if (Communication.isSuccessful(response)) {
			const rowData = response.data || [];
			const remainingCodes =
				codeLimit - rowData.length < 0 ? 0 : codeLimit - rowData.length;
			const newColumnDefs = [
				{
					...columnDefs[0],
					headerName: `Cost Code (${remainingCodes} More Available)`,
				},
			];

			// sort by `Code` string field in place
			rowData.sort((a, b) => a.Code.localeCompare(b.Code));

			this.setState(
				{
					columnDefs: newColumnDefs,
					remainingCodes,
					rowData,
				},
				() => {
					this.gridApi.setColumnDefs(newColumnDefs);
					this.gridApi.sizeColumnsToFit();

					if (callback) {
						callback(rowData);
					}
				}
			);
		} else {
			showError({ response });
		}

		this.setState({
			isLoading: false,
			loadingMessage: '',
		});
	};

	// set gridApi variable to AG Grid's api when the grid loads
	onGridReady = ({ api }) => {
		this.gridApi = api;

		this.setState(
			{
				isLoading: true,
				loadingMessage: 'Loading Cost Codes...',
			},
			this.fetchCostCodes
		);
	};

	// this is called after adding, deleting or importing cost codes
	onUpdateSuccess = () => {
		const { callback } = this.props;

		// display the spinner with a message
		this.setState(
			{
				isLoading: true,
				loadingMessage: 'Updating Cost Codes...',
			},

			// make a network call to reload cost code(s)
			() => this.fetchCostCodes(callback)
		);
	};

	// set AG Grid's sortmodel to default
	onSortChanged = sortModel => {
		this.gridApi.setSortModel(sortModel);
	};

	onSelectionChanged = () => {
		this.setState({ numberOfRows: this.gridApi.getSelectedRows().length });
	};

	launchAddModal = () => {
		const { rowData, customerId, remainingCodes, codeLimit } = this.state;
		const { showModal, showError } = this.context;
		const contactNumber = Util.getFraudNumber();
		let errorMessage = `Your account is limited to ${codeLimit} Cost Codes.
		To add additional Cost Codes, please delete an existing Cost Code or contact
		Customer Support by calling ${contactNumber} Monday–Friday, 8am–8pm Central Time.`;

		if (BillingPlans.isUserInCoreOfficeEssentialPlan()) {
			errorMessage = `Your account is limited to ${codeLimit} Cost Codes. To add additional Cost Codes, please delete an existing Cost Code or${' '}
					<a href="/changeplan">upgrade your plan</a>.`;
		} else if (BillingPlans.isUserInEcommercePlan()) {
			errorMessage = `Your account is limited to ${codeLimit} Cost Codes.`;
		}

		if (remainingCodes === 0) {
			showError({
				data: {
					title: 'Cost Code Limit Exceeded',
					description: errorMessage,
					primaryButtonType: ModalButtonActionType.Ok,
					primaryButtonText: 'Close',
					handleType: 'custom',
				},
			});
		} else {
			showModal({
				component: AddCostCodesModal,
				data: {
					onSuccess: this.onUpdateSuccess,
					rowData,
					remainingCodes,
					customerId,
				},
			});
		}
		Logger.logToServer({
			category: 'ui_interaction',
			action: 'click',
			ui_element: 'add_button',
		});
	};

	launchDeleteModal = () => {
		const { customerId } = this.state;
		const { showModal } = this.context;
		const costCodes = this.gridApi.getSelectedRows().map(obj => ({
			ID: obj.ID,
			Code: obj.Code,
		}));

		showModal({
			component: DeleteCostCodesModal,
			data: {
				costCodes,
				customerId,
				onSuccess: this.onUpdateSuccess,
			},
		});

		Logger.logToServer({
			category: 'ui_interaction',
			action: 'click',
			ui_element: 'delete_button',
		});
	};

	launchImportModal = () => {
		const { customerId, remainingCodes } = this.state;
		const { showModal } = this.context;

		showModal({
			component: ImportCostCodesModal,
			data: {
				onSuccess: this.onUpdateSuccess,
				remainingCodes,
				customerId,
			},
		});

		Logger.logToServer({
			category: 'ui_interaction',
			action: 'click',
			ui_element: 'import_button',
		});
	};

	launchExport = () => {
		const { rowData } = this.state;
		const rows = rowData.map(code => toCsvField(code.Code));
		const parsedRows = rows.join(',');
		const blob = new Blob([parsedRows]);

		Logger.saveActionData('Export Cost Codes Button Clicked');
		Logger.logToServer({
			category: 'ui_interaction',
			action: 'click',
			ui_element: 'export_button',
		});

		if (window.navigator.msSaveOrOpenBlob) {
			window.navigator.msSaveBlob(blob, 'My Cost Codes.csv'); // IE10+ support
		} else {
			const link = window.document.createElement('a');
			link.href = window.URL.createObjectURL(blob, { type: 'text/csv' });
			link.download = 'My Cost Codes.csv';
			document.body.appendChild(link);
			link.click();
			document.body.removeChild(link);
		}

		Logger.logToServer({
			category: 'export_costcode',
			costcode_count: rows.length,
		});
	};

	launchFeedback = () => {
		const { showModal } = this.context;

		launchChatbot({
			flags: this.props.flags,
			showModal,
			trigger: ChatbotTriggers.FeedbackButton,
		});
	};

	render() {
		const { hideRightToolbarButtons } = this.props;
		const { rowData, columnDefs, numberOfRows, isLoading, loadingMessage } =
			this.state;

		// buttons used for WrappedToolbar
		const buttons = [
			{
				label: 'Add',
				icon: 'add-icon',
				disabled:
					!Session.hasCapability(InternalCapability.EditCostCodes) ||
					BillingPlans.isUserInPaygPlan(),
				onClick: this.launchAddModal,
				tooltip: `${'Add'.bold()}\nAdd a new Cost Code`,
				tooltipOptions: {
					position: 'bottom',
				},
				dataqa: 'manage-cost-codes-add',
			},
			{
				label: 'Delete',
				disabled: numberOfRows === 0,
				onClick: this.launchDeleteModal,
				tooltip: `${'Delete'.bold()}\nDelete selected Cost Codes`,
				tooltipOptions: {
					position: 'bottom',
				},
				dataqa: 'manage-cost-codes-delete',
			},
		];

		// hiding right buttons when used in modal for Contacts app
		if (!hideRightToolbarButtons) {
			buttons.push(
				{
					icon: 'import-icon',
					buttonType: WrappedButtonType.TertiaryWhite,
					label: 'Import',
					disabled: BillingPlans.isUserInPaygPlan(),
					group: 'right',
					onClick: this.launchImportModal,
					tooltip: `${'Import'.bold()}\nImport Cost Codes from a csv or text file.`,
					tooltipOptions: {
						position: 'bottom',
					},
				},
				{
					icon: 'export-icon',
					buttonType: WrappedButtonType.TertiaryWhite,
					label: 'Export',
					group: 'right',
					onClick: this.launchExport,
					tooltip: `${'Export'.bold()}\nExport Cost Codes to a csv file.`,
					tooltipOptions: {
						position: 'bottom',
					},
				},
				{
					icon: 'feedback-icon',
					buttonType: WrappedButtonType.TertiaryWhite,
					label: 'Feedback',
					group: 'right',
					onClick: this.launchFeedback,
					tooltip: `${'Feedback'.bold()}\nTell us how we're doing!`,
					tooltipOptions: {
						position: 'bottom',
					},
				}
			);
		}

		return (
			<div className="common-cost-codes">
				<WrappedToolbar buttons={buttons} dataqa="costcodes-toolbar" />
				<GridPanel
					rowData={rowData}
					columnDefs={columnDefs}
					domLayout="autoHeight"
					showPagination={false}
					onGridReady={this.onGridReady}
					onSortChanged={this.onSortChanged}
					onSelectionChanged={this.onSelectionChanged}
					dataqa="costcodes-grid"
				/>
				{isLoading && <Spinner msg={loadingMessage} />}
			</div>
		);
	}
}

export default withLDConsumer()(CostCodes);

CostCodes.contextType = ModalContext;

CostCodes.propTypes = {
	// Hiding right buttons when used in modal
	hideRightToolbarButtons: PropTypes.bool,

	// Callback to send the latest cost codes to caller
	callback: PropTypes.func,
};
